import type { UserCreate } from '@erp/shared';
import { Eye, EyeOff, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/shadcn/index.js';
import { Button, confirmDialog, FormField, Input, Skeleton } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import {
  getUser, listRoles, setUserPassword, updateUser,
  type RoleOption, type UserListItem, type UserPatch,
} from '../../lib/users.js';

import { useUserError } from './userErrors.js';

/** A credential to read out loud once, then never again. */
export interface Handover {
  fullName: string;
  email: string;
  password: string;
}

/** Same two-way toggle both dialogs use for the locale <select>. */
const SELECT_CLASS = 'block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg';

// ---------------------------------------------------------------------------

interface EditProps {
  user: UserListItem;
  /** Editing your own account — the server refuses status/role changes on self. */
  isSelf: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

/**
 * Full edit of one staff user: name, sign-in email, phone, language, status and
 * roles, in one PATCH.
 *
 * Two things make this more than a form:
 *  • The role checkboxes prefill from `GET /users/:id`'s `roleIds`. The LIST only
 *    carries role NAMES, which are editable and non-unique, so they can't be
 *    matched back. If the server omits `roleIds` the picker locks itself and
 *    `roleIds` is left out of the PATCH — a blank picker must never be mistaken
 *    for "remove all their roles".
 *  • Only CHANGED fields are sent. That keeps the audit diff honest and avoids
 *    tripping the self / last-admin guards with a no-op write.
 */
export function EditUserDialog({ user, isSelf, onClose, onSaved }: EditProps): JSX.Element {
  const { t } = useTranslation('users');
  const { t: tc } = useTranslation();
  const userError = useUserError();

  const [roles, setRoles] = useState<RoleOption[] | null>(null);
  const [fullName, setFullName] = useState(user.fullName);
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState(user.phone ?? '');
  const [locale, setLocale] = useState<UserCreate['locale']>(user.locale);
  const [status, setStatus] = useState<'active' | 'disabled'>(
    user.status === 'disabled' ? 'disabled' : 'active',
  );
  /** null = the server didn't tell us, so roles stay untouched on save. */
  const [roleIds, setRoleIds] = useState<string[] | null>(null);
  // Which branches this person may write against. Empty = every one of them,
  // which is the unrestricted state, not "none".
  const [warehouseIds, setWarehouseIds] = useState<string[]>([]);
  const [initialWarehouseIds, setInitialWarehouseIds] = useState<string[]>([]);
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string; code: string }>>([]);
  const [initialRoleIds, setInitialRoleIds] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        // Roles are fetched UNFILTERED even in a directory view (drivers): a
        // filtered list would hide roles this user already holds, and saving
        // would then quietly strip them.
        // The warehouse list is fetched too, but only to LABEL the picker. Note
        // it comes back already narrowed to the editor's own scope — a branch
        // manager cannot hand out a branch they themselves may not touch, which
        // is the same shape as "you may only grant permissions you hold".
        const [detail, options, whList] = await Promise.all([
          getUser(user.id),
          listRoles(),
          api().get<{ items: Array<{ id: string; name: string; code: string }> }>('/warehouses')
            .then((r) => r.data.items)
            .catch(() => []),
        ]);
        if (!alive) return;
        setRoles(options);
        setWarehouses(whList);
        const wh = detail.warehouseIds ?? [];
        setWarehouseIds(wh);
        setInitialWarehouseIds(wh);
        if (detail.roleIds) {
          setRoleIds(detail.roleIds);
          setInitialRoleIds(detail.roleIds);
        }
      } catch (err) {
        if (alive) setError(userError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user.id, userError]);

  const toggleRole = (id: string): void => {
    setRoleIds((cur) => (cur === null ? cur : cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const nextEmail = email.trim().toLowerCase();
  const emailChanged = nextEmail !== user.email.toLowerCase();
  const rolesChanged =
    roleIds !== null && initialRoleIds !== null
    && (roleIds.length !== initialRoleIds.length || roleIds.some((id) => !initialRoleIds.includes(id)));

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (!fullName.trim()) {
      setError(t('edit.errors.nameRequired'));
      return;
    }
    if (!nextEmail) {
      setError(t('edit.errors.emailRequired'));
      return;
    }
    if (!isSelf && rolesChanged && roleIds!.length === 0) {
      setError(t('invite.errors.roleRequired'));
      return;
    }

    const patch: UserPatch = {};
    if (fullName.trim() !== user.fullName) patch.fullName = fullName.trim();
    if (emailChanged) patch.email = nextEmail;
    if ((phone.trim() || null) !== (user.phone ?? null)) patch.phone = phone.trim() || null;
    if (locale !== user.locale) patch.locale = locale;
    // Self edits deliberately carry neither: the server refuses both on yourself
    // (users.cannot_modify_self), so sending them would fail the whole save.
    if (!isSelf && status !== user.status) patch.status = status;
    if (!isSelf && rolesChanged) patch.roleIds = roleIds!;
    const whChanged = warehouseIds.length !== initialWarehouseIds.length
      || warehouseIds.some((id) => !initialWarehouseIds.includes(id));
    if (!isSelf && whChanged) patch.warehouseIds = warehouseIds;

    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    // The sign-in identity is moving — say what that costs before it happens.
    if (emailChanged) {
      const ok = await confirmDialog({
        title: t('edit.confirmEmailTitle'),
        message: t(isSelf ? 'edit.confirmEmailSelf' : 'edit.confirmEmail', {
          name: user.fullName,
          email: nextEmail,
        }),
        confirmLabel: t('edit.submit'),
        tone: 'danger',
      });
      if (!ok) return;
    }

    setSaving(true);
    try {
      const saved = await updateUser(user.id, patch);
      // A server that doesn't yet accept `email` would strip it silently and
      // report success — surface that instead of claiming the change landed.
      // Everything else DID save, so the list still needs refreshing.
      if (patch.email && saved.email?.toLowerCase() !== patch.email) {
        setError(t('edit.errors.emailNotApplied'));
        await onSaved();
        return;
      }
      toast.success(t('edit.saved'));
      onClose();
      await onSaved();
    } catch (err) {
      setError(userError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      {/* Wider than the default: this dialog carries the roles list, which grew
          from six roles to fourteen when the presets landed, and the branch
          list beside it. At max-w-lg both were single-file columns taller than
          the window. */}
      <DialogContent className="max-w-2xl">
        <form onSubmit={onSubmit} noValidate>
          <DialogHeader><DialogTitle>{t('edit.title')}</DialogTitle></DialogHeader>
          {loading ? (
            <Skeleton className="my-4 h-64 w-full" />
          ) : (
            <div className="grid grid-cols-1 gap-3 py-3 sm:grid-cols-2">
              <FormField id="eu-name" label={t('invite.fullName')} required>
                <Input id="eu-name" value={fullName} onChange={(e) => setFullName(e.target.value)} required autoFocus />
              </FormField>
              <FormField id="eu-phone" label={t('invite.phone')}>
                <Input id="eu-phone" type="tel" dir="ltr" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </FormField>
              <div className="sm:col-span-2">
                <FormField id="eu-email" label={t('invite.email')} required>
                  <Input
                    id="eu-email"
                    type="email"
                    dir="ltr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </FormField>
                <p className="mt-1 text-xs text-warning">
                  {t(isSelf ? 'edit.emailWarningSelf' : 'edit.emailWarning')}
                </p>
              </div>
              <FormField id="eu-locale" label={t('invite.locale')}>
                <select
                  id="eu-locale"
                  className={SELECT_CLASS}
                  value={locale}
                  onChange={(e) => setLocale(e.target.value as UserCreate['locale'])}
                >
                  <option value="en">English</option>
                  <option value="ar">العربية</option>
                </select>
              </FormField>
              {!isSelf && (
                <FormField id="eu-status" label={t('edit.status')}>
                  {/* `invited` is never offered: login only accepts `active`, so it
                      is a dead state that just re-creates the un-loggable trap. */}
                  <select
                    id="eu-status"
                    className={SELECT_CLASS}
                    value={status}
                    onChange={(e) => setStatus(e.target.value as 'active' | 'disabled')}
                  >
                    <option value="active">{t('status.active')}</option>
                    <option value="disabled">{t('status.disabled')}</option>
                  </select>
                  {user.status === 'invited' && status === 'active' && (
                    <p className="mt-1 text-xs text-fg-muted">{t('edit.activatesInvited')}</p>
                  )}
                </FormField>
              )}

              <fieldset className="rounded border border-border p-3 sm:col-span-2">
                <legend className="px-2 text-sm font-medium">{t('invite.roles')}</legend>
                {isSelf ? (
                  <p className="text-sm text-fg-muted">{t('edit.selfLimits')}</p>
                ) : roleIds === null ? (
                  <p className="text-sm text-warning">{t('edit.rolesUnavailable')}</p>
                ) : (roles ?? []).length === 0 ? (
                  <p className="text-sm text-fg-muted">{t('invite.noRoles')}</p>
                ) : (
                  <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                    {/* Two columns: fourteen roles in one file is taller than
                        most windows, and every row here is short. */}
                    {roles!.map((r) => (
                      <label key={r.id} className="flex min-w-0 items-center gap-2 text-sm">
                        <input type="checkbox" checked={roleIds.includes(r.id)} onChange={() => toggleRole(r.id)} />
                        <span className="truncate">{r.name}</span>
                        <span className="truncate font-mono text-xs text-fg-muted">{r.code}</span>
                      </label>
                    ))}
                  </div>
                )}
              </fieldset>

              {!isSelf && warehouses.length > 1 && (
                <fieldset className="rounded border border-border p-3 sm:col-span-2">
                  <legend className="px-2 text-sm font-medium">{t('edit.warehouses')}</legend>
                  <p className="mb-2 text-xs text-fg-muted">
                    {warehouseIds.length === 0 ? t('edit.warehousesAll') : t('edit.warehousesHint')}
                  </p>
                  <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                    {warehouses.map((w) => (
                      <label key={w.id} className="flex min-w-0 items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={warehouseIds.includes(w.id)}
                          onChange={() => setWarehouseIds((prev) => (
                            prev.includes(w.id) ? prev.filter((x) => x !== w.id) : [...prev, w.id]
                          ))}
                        />
                        <span className="truncate">{w.name}</span>
                        <span className="truncate font-mono text-xs text-fg-muted">{w.code}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}
            </div>
          )}
          {error && <p className="text-sm text-danger" role="alert">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
              {tc('actions.cancel')}
            </Button>
            <Button type="submit" loading={saving} disabled={loading}>{t('edit.submit')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------

/**
 * Readable alphabet — no `0/O`, `1/l/I`. This password gets spoken across a
 * counter or written on a shift sheet, so ambiguous glyphs cost real time.
 */
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';

function generatePassword(length = 14): string {
  const buf = new Uint32Array(length);
  crypto.getRandomValues(buf);
  return Array.from(buf, (n) => ALPHABET[n % ALPHABET.length]).join('');
}

interface ResetProps {
  user: UserListItem;
  onClose: () => void;
  /** Called with the credential to hand over — shown once by the page. */
  onDone: (handover: Handover) => void | Promise<void>;
}

/**
 * Set someone else's password.
 *
 * The field is plain text behind a Show/Hide toggle rather than permanently
 * masked: the admin is standing next to the person and about to read it out, and
 * a typo they cannot see is worse than a shoulder-surfing risk they can control.
 * Generate fills the SAME field, so what you see is exactly what gets set.
 *
 * Never offered for your own account — this endpoint does not ask for the current
 * password, so allowing it on self would turn any unlocked terminal into a
 * permanent takeover. Your own password lives on the profile page.
 */
export function ResetPasswordDialog({ user, onClose, onDone }: ResetProps): JSX.Element {
  const { t } = useTranslation('users');
  const { t: tc } = useTranslation();
  const userError = useUserError();
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = useCallback(async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (password.trim().length < 8) {
      setError(t('password.tooShort'));
      return;
    }
    const ok = await confirmDialog({
      title: t('password.confirmTitle'),
      message: t('password.confirm', { name: user.fullName }),
      confirmLabel: t('password.submit'),
      tone: 'danger',
    });
    if (!ok) return;
    setSaving(true);
    try {
      const res = await setUserPassword(user.id, password.trim());
      await onDone({ fullName: user.fullName, email: res.email, password: res.password });
      onClose();
    } catch (err) {
      setError(userError(err));
    } finally {
      setSaving(false);
    }
  }, [password, user, t, onDone, onClose, userError]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <form onSubmit={onSubmit} noValidate>
          <DialogHeader>
            <DialogTitle>{t('password.title', { name: user.fullName })}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-3">
            <p className="text-sm text-fg-muted">{t('password.subtitle')}</p>
            <FormField id="pw-new" label={t('password.field')} required>
              <div className="flex items-center gap-2">
                <Input
                  id="pw-new"
                  type={show ? 'text' : 'password'}
                  dir="ltr"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  required
                  autoFocus
                  className="font-mono"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShow((s) => !s)}
                  aria-label={t(show ? 'password.hide' : 'password.show')}
                  title={t(show ? 'password.hide' : 'password.show')}
                >
                  {show ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => { setPassword(generatePassword()); setShow(true); }}
                >
                  <RefreshCw className="h-4 w-4" aria-hidden />
                  {t('password.generate')}
                </Button>
              </div>
            </FormField>
          </div>
          {error && <p className="text-sm text-danger" role="alert">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
              {tc('actions.cancel')}
            </Button>
            <Button type="submit" loading={saving}>{t('password.submit')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
